import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Typography,
  Box,
  Breadcrumbs,
  Link,
  Paper,
  TextField,
  Button,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Card,
  CardContent,
  CardActions,
  Grid,
  Chip,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  NavigateNext as NavigateNextIcon,
  Home as HomeIcon,
  Assessment as AssessmentIcon,
  TableChart as TableChartIcon,
  Assignment as AssignmentIcon,
  Add as AddIcon,
  Search as SearchIcon,
  ExpandMore as ExpandMoreIcon,
  Visibility as VisibilityIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuthenticatedFetch } from '../../../hooks/useAuthenticatedFetch';

interface DataSetSuggestion {
  worksheetName: string;
  startCell: string;
  endCell: string;
  rowCount: number;
  columnCount: number;
  hasHeaders: boolean;
  previewData: unknown[][];
  confidence: number;
  reason: string;
}

interface DiscoveryResult {
  documentId: number;
  autoDiscover: boolean;
  availableWorksheets: string[];
  suggestions: DataSetSuggestion[];
  summary: {
    totalSuggestions: number;
    highConfidenceSuggestions: number;
    worksheetsAnalyzed: number;
    averageConfidence: number;
  };
}

export const CreateDataSetPage: React.FC = () => {
  const navigate = useNavigate();
  const { documentId } = useParams<{ documentId: string }>();
  const { authenticatedFetch } = useAuthenticatedFetch();

  const [loading, setLoading] = useState(true);
  const [discovering, setDiscovering] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Document info
  const [availableWorksheets, setAvailableWorksheets] = useState<string[]>([]);
  
  // Discovery results
  const [discoveryResults, setDiscoveryResults] = useState<DiscoveryResult | null>(null);
  const [selectedSuggestion, setSelectedSuggestion] = useState<DataSetSuggestion | null>(null);
  
  // Manual form state
  const [mode, setMode] = useState<'manual' | 'discovery'>('discovery');
  const [datasetName, setDatasetName] = useState('');
  const [datasetIdentifier, setDatasetIdentifier] = useState('');
  const [worksheetName, setWorksheetName] = useState('');
  const [startCell, setStartCell] = useState('');
  const [endCell, setEndCell] = useState('');
  const [dataDescription, setDataDescription] = useState('');
  
  // Validation errors
  const [nameError, setNameError] = useState('');
  const [identifierError, setIdentifierError] = useState('');
  const [worksheetError, setWorksheetError] = useState('');
  const [startCellError, setStartCellError] = useState('');
  const [endCellError, setEndCellError] = useState('');

  // Preview dialog
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [previewSuggestion, setPreviewSuggestion] = useState<DataSetSuggestion | null>(null);

  // Initialize page - fetch worksheet names from document worksheets table
  useEffect(() => {
    const initialize = async () => {
      if (!documentId) {
        setError('Document ID is required');
        setLoading(false);
        return;
      }

      try {
        // Fetch worksheet names directly using documentId
        const worksheetsResponse = await authenticatedFetch(`/api/appraisals/construction/xlsx/document/${documentId}/worksheets`);

        if (worksheetsResponse && worksheetsResponse.ok) {
          const worksheetsResult = await worksheetsResponse.json();
          if (worksheetsResult.success && worksheetsResult.worksheets) {
            // If no worksheets in identified_document_worksheets table, use special value
            if (worksheetsResult.worksheets.length === 0) {
              setAvailableWorksheets(['First Worksheet in Workbook']);
              setWorksheetName('First Worksheet in Workbook');
            } else {
              setAvailableWorksheets(worksheetsResult.worksheets);
            }
          }
        } else {
          console.warn('Failed to fetch worksheet names, continuing with empty list');
        }

        // Start in manual mode
        setMode('manual');
      } catch (err) {
        console.error('Error initializing page:', err);
        setError(err instanceof Error ? err.message : 'Failed to initialize page');
      } finally {
        setLoading(false);
      }
    };

    initialize();
  }, [documentId, authenticatedFetch]);

  const discoverDataSets = useCallback(async () => {
    try {
      setDiscovering(true);
      setError(null);

      const response = await authenticatedFetch(
        `/api/appraisals/construction/xlsx/document/${documentId}/data-set/identify`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            auto_discover: true
          }),
        }
      );

      if (!response) {
        throw new Error('No response from server');
      }

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `Error: ${response.status}`);
      }

      const result = await response.json();
      if (result.success && result.data) {
        setDiscoveryResults(result.data);
        setAvailableWorksheets(result.data.availableWorksheets);
        
        // If we have suggestions, default to discovery mode
        if (result.data.suggestions.length > 0) {
          setMode('discovery');
        } else {
          setMode('manual');
        }
      } else {
        throw new Error(result.message || 'Failed to discover data sets');
      }
    } catch (err) {
      console.error('Error discovering data sets:', err);
      setError(err instanceof Error ? err.message : 'Failed to discover data sets');
      setMode('manual'); // Fall back to manual mode
    } finally {
      setLoading(false);
      setDiscovering(false);
    }
  }, [authenticatedFetch, documentId]);

  const validateForm = (): boolean => {
    let isValid = true;

    // Validate dataset name
    if (!datasetName.trim()) {
      setNameError('Dataset name is required');
      isValid = false;
    } else {
      setNameError('');
    }

    // Validate dataset identifier
    if (!datasetIdentifier.trim()) {
      setIdentifierError('Dataset identifier is required');
      isValid = false;
    } else if (!/^[a-zA-Z_]+$/.test(datasetIdentifier)) {
      setIdentifierError('Dataset identifier can only contain letters and underscores');
      isValid = false;
    } else {
      setIdentifierError('');
    }

    // Validate worksheet
    if (!worksheetName.trim()) {
      setWorksheetError('Worksheet is required');
      isValid = false;
    } else {
      setWorksheetError('');
    }

    // Validate cells - allow * for both cells to indicate "all data on worksheet"
    const bothAreWildcard = startCell.trim() === '*' && endCell.trim() === '*';
    const cellPattern = /^[A-Z]+[0-9]+$/i;

    if (!startCell.trim()) {
      setStartCellError('Start cell is required');
      isValid = false;
    } else if (startCell.trim() !== '*' && !cellPattern.test(startCell.trim())) {
      setStartCellError('Invalid cell format (e.g., A1, B3, or * for entire worksheet)');
      isValid = false;
    } else {
      setStartCellError('');
    }

    if (!endCell.trim()) {
      setEndCellError('End cell is required');
      isValid = false;
    } else if (endCell.trim() !== '*' && !cellPattern.test(endCell.trim())) {
      setEndCellError('Invalid cell format (e.g., D10, Z50, or * for entire worksheet)');
      isValid = false;
    } else {
      setEndCellError('');
    }

    // If one cell is wildcard but not both, show error
    if ((startCell.trim() === '*' && endCell.trim() !== '*') ||
        (startCell.trim() !== '*' && endCell.trim() === '*')) {
      setStartCellError('Both cells must be * to select entire worksheet');
      setEndCellError('Both cells must be * to select entire worksheet');
      isValid = false;
    }

    return isValid;
  };

  const handleCreateDataSet = async () => {
    if (!validateForm()) return;

    setSubmitting(true);
    setError(null);

    try {
      const request = {
        dataset_name: datasetName.trim(),
        dataset_identifier: datasetIdentifier.trim(),
        worksheet_name: worksheetName.trim() === 'First Worksheet in Workbook' ? null : worksheetName.trim(),
        start_cell: startCell.trim(),
        end_cell: endCell.trim(),
        data_description: dataDescription.trim() || null
      };

      const response = await authenticatedFetch(
        `/api/appraisals/construction/xlsx/document/${documentId}/data-set/create`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(request),
        }
      );

      if (!response) {
        throw new Error('No response from server');
      }

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `Error: ${response.status}`);
      }

      const result = await response.json();
      if (result.success) {
        // Navigate to edit page for the newly created data set
        navigate(`/admin/deliverables/doc-id-system/documents/${documentId}/data-set/${result.datasetId}/edit`);
      } else {
        throw new Error(result.message || 'Failed to create data set');
      }
    } catch (err) {
      console.error('Error creating data set:', err);
      setError(err instanceof Error ? err.message : 'Failed to create data set');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUseSuggestion = (suggestion: DataSetSuggestion) => {
    setDatasetName(''); // User will need to provide this
    setDatasetIdentifier(''); // User will need to provide this
    setWorksheetName(suggestion.worksheetName);
    setStartCell(suggestion.startCell);
    setEndCell(suggestion.endCell);
    setDataDescription('');
    setSelectedSuggestion(suggestion);
    setMode('manual');
  };

  const handlePreviewSuggestion = (suggestion: DataSetSuggestion) => {
    setPreviewSuggestion(suggestion);
    setPreviewDialogOpen(true);
  };

  const getConfidenceColor = (confidence: number) => {
    if (confidence >= 0.8) return 'success';
    if (confidence >= 0.6) return 'warning';
    return 'error';
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Breadcrumbs */}
      <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} sx={{ mb: 3 }}>
        <Link
          color="inherit"
          href="/"
          onClick={(e) => {
            e.preventDefault();
            navigate('/');
          }}
          sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer' }}
        >
          <HomeIcon fontSize="inherit" />
          Home
        </Link>
        <Link
          color="inherit"
          href="/admin/deliverables"
          onClick={(e) => {
            e.preventDefault();
            navigate('/admin/deliverables');
          }}
          sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer' }}
        >
          <AssessmentIcon fontSize="inherit" />
          Deliverables Admin
        </Link>
        <Link
          color="inherit"
          href="/admin/deliverables/doc-id-system"
          onClick={(e) => {
            e.preventDefault();
            navigate('/admin/deliverables/doc-id-system');
          }}
          sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer' }}
        >
          <TableChartIcon fontSize="inherit" />
          Document Identification System
        </Link>
        <Link
          color="inherit"
          href={`/admin/deliverables/doc-id-system/documents/${documentId}`}
          onClick={(e) => {
            e.preventDefault();
            navigate(`/admin/deliverables/doc-id-system/documents/${documentId}`);
          }}
          sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer' }}
        >
          <AssignmentIcon fontSize="inherit" />
          Document Details
        </Link>
        <Typography color="text.primary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <AddIcon fontSize="inherit" />
          Create Data Set
        </Typography>
      </Breadcrumbs>

      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          Create Data Set
        </Typography>
        <Box>
          <Button
            variant={mode === 'discovery' ? 'contained' : 'outlined'}
            onClick={() => setMode('discovery')}
            disabled={!discoveryResults || discoveryResults.suggestions.length === 0}
            sx={{ mr: 1 }}
          >
            Use Suggestions
          </Button>
          <Button
            variant={mode === 'manual' ? 'contained' : 'outlined'}
            onClick={() => setMode('manual')}
          >
            Manual Entry
          </Button>
        </Box>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {mode === 'discovery' && discoveryResults && (
        <Box mb={3}>
          {/* Discovery Summary */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Discovery Results
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6} sm={3}>
                  <Typography variant="h4" color="primary">
                    {discoveryResults.summary.totalSuggestions}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Suggestions Found
                  </Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="h4" color="success.main">
                    {discoveryResults.summary.highConfidenceSuggestions}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    High Confidence
                  </Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="h4" color="info.main">
                    {discoveryResults.summary.worksheetsAnalyzed}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Worksheets Analyzed
                  </Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="h4" color="warning.main">
                    {Math.round(discoveryResults.summary.averageConfidence * 100)}%
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Avg. Confidence
                  </Typography>
                </Grid>
              </Grid>
              
              <Box mt={2}>
                <Button
                  variant="outlined"
                  startIcon={<SearchIcon />}
                  onClick={discoverDataSets}
                  disabled={discovering}
                >
                  {discovering ? 'Rediscovering...' : 'Rediscover'}
                </Button>
              </Box>
            </CardContent>
          </Card>

          {/* Suggestions */}
          {discoveryResults.suggestions.length === 0 ? (
            <Alert severity="info">
              No data set suggestions found. Try manual entry instead.
            </Alert>
          ) : (
            <Grid container spacing={2}>
              {discoveryResults.suggestions.map((suggestion, index) => (
                <Grid item xs={12} md={6} lg={4} key={index}>
                  <Card>
                    <CardContent>
                      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                        <Typography variant="h6" noWrap>
                          {suggestion.worksheetName}
                        </Typography>
                        <Chip
                          label={`${Math.round(suggestion.confidence * 100)}%`}
                          color={getConfidenceColor(suggestion.confidence)}
                          size="small"
                        />
                      </Box>
                      
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Range: {suggestion.startCell}:{suggestion.endCell}
                      </Typography>
                      
                      <Typography variant="body2" gutterBottom>
                        Size: {suggestion.rowCount} × {suggestion.columnCount}
                      </Typography>
                      
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        {suggestion.hasHeaders ? 'Has headers' : 'No headers detected'}
                      </Typography>
                      
                      <Typography variant="body2" color="text.secondary">
                        {suggestion.reason}
                      </Typography>
                    </CardContent>
                    
                    <CardActions>
                      <Button
                        size="small"
                        startIcon={<VisibilityIcon />}
                        onClick={() => handlePreviewSuggestion(suggestion)}
                      >
                        Preview
                      </Button>
                      <Button
                        size="small"
                        startIcon={<CheckCircleIcon />}
                        onClick={() => handleUseSuggestion(suggestion)}
                      >
                        Use This
                      </Button>
                    </CardActions>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </Box>
      )}

      {mode === 'manual' && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Manual Data Set Creation
            </Typography>

            {selectedSuggestion && (
              <Alert severity="info" sx={{ mb: 3 }}>
                Using suggestion from {selectedSuggestion.worksheetName} 
                ({selectedSuggestion.startCell}:{selectedSuggestion.endCell})
              </Alert>
            )}

            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Dataset Name"
                  value={datasetName}
                  onChange={(e) => setDatasetName(e.target.value)}
                  error={!!nameError}
                  helperText={nameError || 'Human-readable name for this dataset (e.g., "Cost Data", "Revenue Table")'}
                  margin="normal"
                  required
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Dataset Identifier"
                  value={datasetIdentifier}
                  onChange={(e) => setDatasetIdentifier(e.target.value)}
                  error={!!identifierError}
                  helperText={identifierError || 'Use only letters and underscores (e.g., cost_data, revenue_table)'}
                  margin="normal"
                  required
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth margin="normal" error={!!worksheetError} required>
                  <InputLabel>Worksheet</InputLabel>
                  <Select
                    value={worksheetName}
                    onChange={(e) => setWorksheetName(e.target.value)}
                    label="Worksheet"
                  >
                    {availableWorksheets.map((ws) => (
                      <MenuItem key={ws} value={ws}>
                        {ws}
                      </MenuItem>
                    ))}
                  </Select>
                  {worksheetError && <FormHelperText>{worksheetError}</FormHelperText>}
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Data Description"
                  value={dataDescription}
                  onChange={(e) => setDataDescription(e.target.value)}
                  helperText="Optional description of what this dataset contains"
                  margin="normal"
                  multiline
                  rows={3}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Start Cell"
                  value={startCell}
                  onChange={(e) => setStartCell(e.target.value.toUpperCase())}
                  error={!!startCellError}
                  helperText={startCellError || 'e.g., A1, B3, or * for entire worksheet'}
                  placeholder="A1 or *"
                  margin="normal"
                  required
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="End Cell"
                  value={endCell}
                  onChange={(e) => setEndCell(e.target.value.toUpperCase())}
                  error={!!endCellError}
                  helperText={endCellError || 'e.g., D10, Z50, or * for entire worksheet'}
                  placeholder="T35 or *"
                  margin="normal"
                  required
                />
              </Grid>
            </Grid>

            <Divider sx={{ my: 3 }} />

            <Box display="flex" justifyContent="space-between">
              <Button
                variant="outlined"
                onClick={() => navigate(`/admin/deliverables/doc-id-system/documents/${documentId}`)}
                disabled={submitting}
              >
                Cancel
              </Button>
              
              <Button
                variant="contained"
                onClick={handleCreateDataSet}
                disabled={submitting}
                startIcon={submitting ? <CircularProgress size={20} /> : <AddIcon />}
              >
                {submitting ? 'Creating...' : 'Create Data Set'}
              </Button>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Preview Dialog */}
      <Dialog
        open={previewDialogOpen}
        onClose={() => setPreviewDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Data Preview: {previewSuggestion?.worksheetName}
        </DialogTitle>
        <DialogContent>
          {previewSuggestion && (
            <>
              <Box mb={2}>
                <Typography variant="body2">
                  <strong>Range:</strong> {previewSuggestion.startCell}:{previewSuggestion.endCell}
                </Typography>
                <Typography variant="body2">
                  <strong>Size:</strong> {previewSuggestion.rowCount} × {previewSuggestion.columnCount}
                </Typography>
                <Typography variant="body2">
                  <strong>Confidence:</strong> {Math.round(previewSuggestion.confidence * 100)}%
                </Typography>
              </Box>
              
              {previewSuggestion.previewData.length > 0 && (
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableBody>
                      {previewSuggestion.previewData.slice(0, 10).map((row, rowIndex) => (
                        <TableRow key={rowIndex}>
                          {row.slice(0, 10).map((cell, cellIndex) => (
                            <TableCell key={cellIndex} sx={{ maxWidth: 100, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {String(cell || '')}
                            </TableCell>
                          ))}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewDialogOpen(false)}>
            Close
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              if (previewSuggestion) {
                handleUseSuggestion(previewSuggestion);
                setPreviewDialogOpen(false);
              }
            }}
          >
            Use This Data Set
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};